import React from 'react';
import { Languages, GraduationCap, Cpu, Landmark, Brain } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const PillarsSection = () => {
  const { t } = useLanguage();

  const pillars = [
    {
      icon: Languages,
      title: t('pillars.pillar1.title'),
      description: t('pillars.pillar1.description'),
      color: 'blue',
    },
    {
      icon: GraduationCap,
      title: t('pillars.pillar2.title'),
      description: t('pillars.pillar2.description'),
      color: 'red',
    },
    {
      icon: Cpu,
      title: t('pillars.pillar3.title'),
      description: t('pillars.pillar3.description'),
      color: 'green',
    },
    {
      icon: Landmark,
      title: t('pillars.pillar4.title'),
      description: t('pillars.pillar4.description'),
      color: 'orange',
    },
    {
      icon: Brain,
      title: t('pillars.pillar5.title'),
      description: t('pillars.pillar5.description'),
      color: 'blue',
    },
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'blue':
        return { bg: 'bg-oar-blue-soft', text: 'text-oar-blue', border: 'border-oar-blue/20' };
      case 'red':
        return { bg: 'bg-oar-red-soft', text: 'text-oar-red', border: 'border-oar-red/20' };
      case 'green':
        return { bg: 'bg-oar-green-soft', text: 'text-oar-green', border: 'border-oar-green/20' };
      case 'orange':
        return { bg: 'bg-oar-orange-soft', text: 'text-oar-orange', border: 'border-oar-orange/20' };
      default:
        return { bg: 'bg-oar-blue-soft', text: 'text-oar-blue', border: 'border-oar-blue/20' };
    }
  };

  return (
    <section
      data-testid="pillars-section"
      className="py-24 md:py-32 bg-oar-bg-secondary relative"
    >
      {/* Tricolor line top */}
      <div className="tricolor-line absolute top-0 left-0 right-0" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-blue font-bold">
            Architecture Stratégique
          </span>
          <h2
            data-testid="pillars-title"
            className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-oar-text-primary mt-4 mb-6"
          >
            {t('pillars.title')}
          </h2>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-oar-text-secondary">
            {t('pillars.subtitle')}
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {pillars.map((pillar, index) => {
            const colors = getColorClasses(pillar.color);
            return (
              <div
                key={index}
                data-testid={`pillar-card-${index + 1}`}
                className={`group bg-white border ${colors.border} rounded-lg p-8 md:p-10 transition-all duration-300 card-hover relative ${
                  index === 0 ? 'lg:col-span-2' : ''
                } ${index === 4 ? 'lg:col-span-2' : ''}`}
              >
                {/* Icon */}
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-lg mb-6 ${colors.bg} ${colors.text}`}
                >
                  <pillar.icon className="w-7 h-7" />
                </div>

                {/* Number */}
                <div className="text-6xl font-heading font-bold text-oar-border/50 absolute top-6 right-6 group-hover:text-oar-blue/20 transition-colors">
                  0{index + 1}
                </div>

                {/* Content */}
                <h3 className={`font-heading text-xl md:text-2xl font-medium ${colors.text} mb-4 relative`}>
                  {pillar.title}
                </h3>
                <p className="text-oar-text-secondary leading-relaxed">
                  {pillar.description}
                </p>

                {/* Bottom accent */}
                <div className={`w-12 h-1 ${colors.bg} mt-6 group-hover:w-24 transition-all duration-300 rounded-full`} />
              </div>
            );
          })}
        </div>
      </div>

      {/* African line bottom */}
      <div className="africa-line absolute bottom-0 left-0 right-0" />
    </section>
  );
};
